import { NotFound, ServerError } from "../components/ErrorStates.jsx";

/**
 * Small UI helpers the hand-off components don't cover: bootstrap/loading
 * states, inline API messages, and a 403 state for the admin guard.
 * They reuse the design tokens only — no new visual language.
 */

export function PageLoading({ label = "Loading…" }) {
  return (
    <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "24px 0" }}>
      {label}
    </p>
  );
}

export function BootLoading() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "var(--text-sm)",
        color: "var(--text-secondary)",
      }}
    >
      Loading…
    </div>
  );
}

const TONE = {
  danger: { background: "var(--bg-danger)", color: "var(--text-danger)" },
  warning: { background: "var(--bg-warning)", color: "var(--text-warning)" },
  success: { background: "var(--bg-success)", color: "var(--text-success)" },
  info: { background: "var(--surface-1)", color: "var(--text-secondary)" },
};

export function InlineMessage({ tone = "danger", children, style }) {
  if (!children) return null;
  const palette = TONE[tone] || TONE.info;
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      style={{
        background: palette.background,
        color: palette.color,
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        padding: "10px 14px",
        fontSize: "var(--text-sm)",
        marginBottom: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Error envelope -> one user-facing line. The API's `message` is what the user
 * should read; `details` (e.g. password-policy violations) is appended when set.
 */
export function errorText(error, fallback = "Something went wrong.") {
  if (!error) return fallback;
  const message = error.message || fallback;
  const details = Array.isArray(error.details) ? error.details.filter(Boolean) : [];
  return details.length ? `${message} ${details.join(" ")}` : message;
}

/** Renders a ServerError (retry) or NotFound (back) for a failed load. */
export function ErrorView({ error, onRetry, onBack }) {
  if (error?.isNotFound || error?.code === "not_found" || error?.status === 404) {
    return <NotFound onBack={onBack} />;
  }
  return <ServerError onRetry={onRetry} />;
}

/**
 * `useApi` result -> loading / error / render-prop.
 * Usage: <AsyncBoundary query={q}>{(data) => ...}</AsyncBoundary>
 */
export function AsyncBoundary({ query, onBack, children, loadingLabel }) {
  if (query.loading) return <PageLoading label={loadingLabel} />;
  if (query.error) {
    return (
      <div>
        <InlineMessage tone="danger">{query.error.message}</InlineMessage>
        <ErrorView error={query.error} onRetry={query.reload} onBack={onBack} />
      </div>
    );
  }
  return typeof children === "function" ? children(query.data) : children;
}

export function Forbidden({ children }) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "28px 16px",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
      }}
    >
      <p style={{ fontSize: "var(--text-md)", fontWeight: 500, margin: "10px 0 4px" }}>
        403 — Admins only
      </p>
      <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: 0 }}>
        {children ?? "Your account doesn't have access to the operations panel."}
      </p>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 20,
        gap: 12,
      }}
    >
      <div>
        <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-lg)" }}>{title}</p>
        {subtitle ? (
          <p style={{ margin: "2px 0 0", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions}
    </div>
  );
}
